import { isAfter } from "date-fns";
import { z, ZodIssueCode } from "zod";
import { UNMASKED_PHONE_NUMBER_LENGTH } from "../../modules/common/inputs/PhoneNumberInput";
import {
  phonesOwnerType,
  phonesPhoneType,
  phonesRestrictionFrequency,
} from "../api/models";
import {
  accountNumberMaxLength,
  accountNumberMinLength,
  billingZipCodeLength,
  cardLast4Length,
  noWhitespaceRegex,
  numbersOnlyRegex,
  routingNumberLength,
  ssnLast4Length,
} from "../constants/lengths";
import { dollarStringToCents } from "../formatting";

const NUMERICAL_ONLY_REGEX = /^\d+$/;
const NO_REPEATING_PHONE = /^(?!^(\d)\1+$)\d+$/;
export const CMINFO_OTHER_LANGUAGE_PREFERENCE_ID = "3";

export const changeUsernameSchema = z
  .string()
  .min(6, "New username must be at least 6 characters long.")
  .max(20, "New username must not be longer than 20 characters.")
  .regex(noWhitespaceRegex, "Username must not include spaces.");

export const firstNameValidationSchema = z
  .string()
  .min(1, "First name must not be empty.");

export const lastNameValidationSchema = z
  .string()
  .min(1, "Last name must not be empty.");

export const routingNumberSchema = z
  .string()
  .length(
    routingNumberLength,
    `Routing number must be ${routingNumberLength} numbers long.`,
  )
  .regex(noWhitespaceRegex, "Routing number must not include spaces.")
  .regex(numbersOnlyRegex, "Only numbers allowed.");

export const accountNumberSchema = z
  .string()
  .min(
    accountNumberMinLength,
    `Account number must be at least ${accountNumberMinLength} numbers long.`,
  )
  .max(
    accountNumberMaxLength,
    `Account number must not be longer than ${accountNumberMaxLength} numbers.`,
  )
  .regex(noWhitespaceRegex, "Account number must not include spaces.")
  .regex(numbersOnlyRegex, "Only numbers allowed.");

export const creditLineIncreaseNewCreditLimitSchema = z
  .object({
    currCreditLimit: z.string(),
    maxCurrCreditLimit: z.string(),
    newCreditLimit: z.string(),
  })
  .superRefine(
    (
      arg,
      ctx,
    ): arg is {
      currCreditLimit: string;
      maxCurrCreditLimit: string;
      newCreditLimit: string;
    } => {
      if (!arg.newCreditLimit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Please enter a numeric value in the New Credit Limit field.",
          path: ["newCreditLimitAmountCents"],
          fatal: true,
        });

        return z.NEVER;
      }

      if (
        dollarStringToCents(arg.currCreditLimit) >=
          dollarStringToCents(arg.newCreditLimit) ||
        dollarStringToCents(arg.currCreditLimit) === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `The New Credit Limit cannot be equal to or less than the current credit limit of ${arg.currCreditLimit}`,
          path: ["newCreditLimitAmountCents"],
          fatal: true,
        });

        return z.NEVER;
      }

      if (
        dollarStringToCents(arg.newCreditLimit) >
        dollarStringToCents(arg.maxCurrCreditLimit)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `The New Credit Limit cannot exceed the maximum of ${arg.maxCurrCreditLimit}`,
          path: ["newLimitCents"],
          fatal: true,
        });
      }

      return z.NEVER;
    },
  );

export const creditLineIncreaseReasonSchema = z
  .string()
  .transform((arg) => arg.length)
  .refine((arg) => arg >= 1, {
    message: "Please Select a Reason for the Credit Line Increase request.",
    path: ["reasonId"],
  });

export const creditLineIncreaseContactPhoneSchema = z
  .string()
  .superRefine((arg, ctx): arg is string => {
    if (arg.length >= 1 && arg.length < UNMASKED_PHONE_NUMBER_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "The Contact Phone Number must be a 10 digit number, (example (605) 555-1212.) Please enter a valid phone number.",
        path: ["phoneNumber"],
        fatal: true,
      });
    }

    return z.NEVER;
  });

export const phonesMemoSchema = z.optional(
  z.string().max(4000, "Our memo may not contain more than 4000 characters."),
);

export const addEditPhoneRequestSchema = z
  .object({
    active: z.boolean(),
    phoneNumber: z
      .string()
      .min(
        10,
        "The telephone number must contain 10 numbers and all numbers may not be the same.",
      )
      .regex(
        NO_REPEATING_PHONE,
        "The telephone number must contain 10 numbers and all numbers may not be the same.",
      )
      .min(1, "A complete phone number is required."),
    extension: z
      .string()
      .max(4, "The extension must be a numeric value between 0 and 9999.")
      .refine(
        (arg) => arg.length === 0 || RegExp(NUMERICAL_ONLY_REGEX).test(arg),
        "The extension may contain numbers only.",
      ),
    inactivationReasonId: z.optional(z.string()),
    ownerFirstName: z.string(),
    ownerLastName: z.string(),
    ownerType: phonesOwnerType
      .optional()
      .refine(
        (arg) => !!arg,
        "You must indicate the owner of the telephone number.",
      ),
    phoneType: phonesPhoneType
      .optional()
      .refine((arg) => !!arg, "You must indicate the telephone number type."),
    memo: phonesMemoSchema,
    reactivationDate: z.string().optional().nullable(),
    restricted: z.boolean(),
    restrictionDays: z.array(z.string()),
    restrictionFrequency: phonesRestrictionFrequency,
  })
  .superRefine((req, ctx) => {
    if (req.phoneType === "CELL") {
      if (req.ownerFirstName.length > 20) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First name is limited to 20 characters in length.",
          path: ["ownerFirstName"],
        });
      }
      if (req.ownerLastName.length > 20) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last name is limited to 20 characters in length.",
          path: ["ownerLastName"],
        });
      }
    }
    if (!req.active) {
      if (!req.inactivationReasonId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "You must select an inactivation reason.",
          path: ["inactivationReasonId"],
        });
      }
      if (req.reactivationDate === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "You must provide a reactivation date.",
          path: ["reactivationDate"],
        });
      }
    }
    if (
      req.restricted &&
      req.restrictionDays.length === 0 &&
      req.restrictionFrequency === "NONE"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Restriction reason is invalid, your changes will not be saved.",
        path: ["restricted"],
      });
    }
  });

export const accountSearchRequestSchema = z
  .string()
  .min(1)
  .regex(NUMERICAL_ONLY_REGEX, "No non-numeric characters are allowed.");

export const accountSearchCardLast4Schema = z
  .string()
  .refine(
    (v) => v.length < 1 || v.length >= cardLast4Length,
    "Last 4 card # must be a 4 digit number.",
  );

export const accountSearchSSNLast4Schema = z
  .string()
  .refine(
    (v) => v.length < 1 || v.length >= ssnLast4Length,
    "Last 4 SSN must be a 4 digit number.",
  );

export const accountSearchBirthDateSchema = z.string().refine((v) => {
  if (v) {
    return !isAfter(new Date(v), new Date());
  }
  return true;
}, "The Date of Birth cannot be in the future.");

export const accountSearchBillingZipCodeSchema = z
  .string()
  .refine(
    (v) => v.length < 1 || v.length === billingZipCodeLength,
    "Billing ZIP must be a 5 digit number.",
  );

export const accountSearchThreeofFourRequestSchema = z.object({
  cardLast4: accountSearchCardLast4Schema,
  ssnLast4: accountSearchSSNLast4Schema,
  birthDate: accountSearchBirthDateSchema,
  billingZipCode: accountSearchBillingZipCodeSchema,
});

export const lostStolenSchema = z.object({
  fraudCharges: z
    .string()
    .regex(
      /^[A-z0-9 !@#$%^&[(){}?/'"\r\n=+.,;:<>`~|-]+$/,
      "Fraud Charges contains invalid characters.",
    ),
});

export const cmInfoLanguageRequestSchema = z
  .object({
    languagePreferenceId: z.string(),
    otherLanguage: z.string(),
    languageLastVerifiedDate: z.string().nullable(),
    languageDueForVerification: z.boolean(),
    isLanguageVerified: z.boolean(),
  })
  .strict()
  .superRefine((req, ctx) => {
    if (
      req.languagePreferenceId === CMINFO_OTHER_LANGUAGE_PREFERENCE_ID &&
      (req.otherLanguage.length < 1 ||
        !RegExp(/^[a-zA-Z0-9!@#$%^&()[\]{}?/|\-_=+,.;:<>`~' ]*$/).test(
          req.otherLanguage,
        ))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Other language preference is blank or contains invalid characters",
        path: ["otherLanguage"],
      });
    }
  });

export const cmInfoBillingAddressRequestSchema = z
  .object({
    addressLine1: z
      .string()
      .trim()
      .regex(
        /^[a-zA-Z0-9!@#$%^&()[\]{}?/\\|\-_=+,.;:<>~' ]+$/,
        "A_0204 - Address is blank or contains invalid characters.",
      ),
    addressLine2: z
      .string()
      .regex(
        /^[a-zA-Z0-9!@#$%^&()[\]{}?/\\|\-_=+,.;:<>`~'" ]*$/,
        "A_0205 - Ste. contains invalid characters.",
      ),
    city: z
      .string()
      .trim()
      .regex(
        /^[a-zA-Z0-9\- ]+$/,
        "A_0206 - City is blank or contains invalid characters. (Valid characters include alpha characters, space, apostrophe, and dash.)",
      ),
    state: z.string().min(1, "A_0207 - State has not been selected"),
    zip: z
      .string()
      .regex(
        /^\d{5}(-\d*)?$/,
        "A_0208 - Zip Code is not numeric or the first five characters are not populated",
      ),
    billingAddressLastVerifiedDate: z.string().nullable(),
    billingAddressDueForVerification: z.boolean(),
    isBillingAddressVerified: z.boolean(),
  })
  .strict();

export const cmInfoEmailRequestSchema = z
  .object({
    primaryEmail: z
      .string()
      .regex(
        /^$|^([^\s@\\*[\].<>"#]+\.)*[^\s@\\*[\].<>"#]+@([^\s@\\*[\].<>"#]+\.)+[^\s@\\*[\].<>"#]+$/,
        "A_0211 - Email Address contains invalid characters or is not formatted correctly. Please correct the email address.",
      ),
    primaryEmailLastVerifiedDate: z.string().nullable(),
    primaryEmailDueForVerification: z.boolean(),
    isPrimaryEmailVerified: z.boolean(),
    alternateEmail: z
      .string()
      .regex(
        /^$|^([^\s@\\*[\].<>"#]+\.)*[^\s@\\*[\].<>"#]+@([^\s@\\*[\].<>"#]+\.)+[^\s@\\*[\].<>"#]+$/,
        "A_0211 - Email Address contains invalid characters or is not formatted correctly. Please correct the email address.",
      ),
    alternateEmailLastVerifiedDate: z.string().nullable(),
    alternateEmailDueForVerification: z.boolean(),
    isAlternateEmailVerified: z.boolean(),
    emailOptOut: z.boolean(),
    emailOptOutLastVerifiedDate: z.string().nullable(),
    emailOptOutDueForVerification: z.boolean(),
    isEmailOptOutVerified: z.boolean(),
    isPaperless: z.boolean().nullable(),
  })
  .strict()
  .superRefine((req, ctx) => {
    if (req.primaryEmail === "" && req.alternateEmail !== "") {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          "A_0158 - You must enter a Primary email address when an account has an Alternate email address. Verify Alternate email address.\nIf the Alternate e-mail is valid, enter that value as the Primary email address.\nIf the Alternate e-mail is not valid, remove the Alternate e-mail address.\nClick Update button to continue.",
        path: ["alternateEmail"],
      });

      return z.NEVER;
    } else if (
      req.primaryEmail !== "" &&
      req.primaryEmail === req.alternateEmail
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          "A_0184 - The Alternate email may not be the same as the Primary email. Please provide a different email address for the Alternate email or leave the field blank",
        path: ["alternateEmail"],
      });
    }
  });

export const cmInfoP2DRequestSchema = z
  .object({
    permissionToDiscussLastVerifiedDate: z.string().nullable(),
    permissionToDiscussDueForVerification: z.boolean(),
    isPermissionToDiscussVerified: z.boolean(),
  })
  .strict();

export const cmInfoEmploymentIncomeRequestSchema = z
  .object({
    source: z
      .string()
      .regex(
        /^[a-zA-Z0-9!@#$%^&()[\]{}?/|\-_=+,.;:<>`~'\n ]*$/,
        "A_0172 - Place of employment/Source of Income contains invalid characters.",
      ),
    incomeLastVerifiedDate: z.string().nullable(),
    incomeDueForVerification: z.boolean(),
    isIncomeVerified: z.boolean(),
  })
  .strict();

export const cmInfoSchoolRequestSchema = z
  .object({
    school: z
      .string()
      .regex(
        /^[a-zA-Z0-9%^&()[\]{}?/\\|\-_=+,.;:<>~' ]*$/,
        "A_0173 - School Name contains invalid characters.",
      ),
    graduationDate: z.string(),
    graduationStatus: z.enum(["grad", "NA"]).nullable(),
    schoolLastVerifiedDate: z.string().nullable(),
    schoolDueForVerification: z.boolean(),
    isSchoolVerified: z.boolean(),
  })
  .strict()
  .superRefine((req, ctx) => {
    if (req.school !== "" && req.graduationStatus === null) {
      if (
        req.graduationDate === "" ||
        !RegExp(/^((0[1-9])|(1[0-2]))\/[0-9]{2}$/).test(req.graduationDate)
      ) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message:
            "A_0166 - Please enter a two digit (numeric) month between 01 and 12 and a two digit year between 00 and 99",
          path: ["graduationDate"],
        });

        return z.NEVER;
      }

      const month = Number.parseInt(req.graduationDate.substring(0, 2));
      const partialYear = Number.parseInt(req.graduationDate.substring(3, 5));
      const year = partialYear >= 0 ? 2000 + partialYear : 1900 + partialYear;

      const now = new Date();

      if (
        year < now.getFullYear() ||
        (year === now.getFullYear() && month < now.getMonth() + 1)
      ) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message:
            'A_0212 - Graduation Dt provided is in the past. Please select "Grad" or correct the Grad Dt.',
          path: ["graduationDate"],
        });

        return z.NEVER;
      }
    }
  });

export const cmInfoContactPreferenceRequestSchema = z
  .object({
    contactPreference: z.record(z.string(), z.number().nullable()),
    contactPreferenceLastVerifiedDate: z.string().nullable(),
    contactPreferenceDueForVerification: z.boolean(),
    isContactPreferenceVerified: z.boolean(),
  })
  .strict()
  .superRefine((req, ctx) => {
    let isError = false;

    if (
      req.contactPreference["TELEPHONE_CALL"] !== null &&
      (req.contactPreference["TELEPHONE_CALL"] > 4 ||
        req.contactPreference["TELEPHONE_CALL"] < 1)
    ) {
      isError = true;
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          "C_09010 - Telephone Call must be ranked between 1 (most preferred) and 4 (least preferred)",
        path: ["contactPreference_TELEPHONE_CALL"],
      });
    }

    if (
      req.contactPreference["EMAIL"] !== null &&
      (req.contactPreference["EMAIL"] > 4 || req.contactPreference["EMAIL"] < 1)
    ) {
      isError = true;
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          "C_09011 - Email must be ranked between 1 (most preferred) and 4 (least preferred)",
        path: ["contactPreference_EMAIL"],
      });
    }

    if (
      req.contactPreference["TEXT_MESSAGE"] !== null &&
      (req.contactPreference["TEXT_MESSAGE"] > 4 ||
        req.contactPreference["TEXT_MESSAGE"] < 1)
    ) {
      isError = true;
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          "C_09012 - Text Message must be ranked between 1 (most preferred) and 4 (least preferred)",
        path: ["contactPreference_TEXT_MESSAGE"],
      });
    }

    if (
      req.contactPreference["LETTER"] !== null &&
      (req.contactPreference["LETTER"] > 4 ||
        req.contactPreference["LETTER"] < 1)
    ) {
      isError = true;
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          "C_09013 - Letter must be ranked between 1 (most preferred) and 4 (least preferred)",
        path: ["contactPreference_LETTER"],
      });
    }

    const contactPreferenceSet = new Set<number>();
    for (const key in req.contactPreference) {
      if (req.contactPreference[key] !== null) {
        contactPreferenceSet.add(req.contactPreference[key]);
      }
    }
    if (
      contactPreferenceSet.size !==
      Object.values(req.contactPreference).filter((item) => item !== null)
        .length
    ) {
      isError = true;
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          'C_09015 - Please rate your preference using "1" to indicate your most preferred method of contact, "2" to indicate your second preferred method of contact, "3" to indicate your third preferred method of contact, and "4" to indicate your fourth preferred method of contact.',
        path: ["contactPreference_DUPLICATION"],
      });
    }

    if (isError) {
      return z.NEVER;
    }
  });

export const cmInfoSecurityRequestSchema = z
  .object({
    dateOfBirthLastVerifiedDate: z.string().nullable(),
    dateOfBirthDueForVerification: z.boolean(),
    isDateOfBirthVerified: z.boolean(),
    mothersMaidenNameLastVerifiedDate: z.string().nullable(),
    mothersMaidenNameDueForVerification: z.boolean(),
    isMothersMaidenNameVerified: z.boolean(),
    securityLastVerifiedDate: z.string().nullable(),
    securityDueForVerification: z.boolean(),
    isSecurityVerified: z.boolean(),
    ssnLast4LastVerifiedDate: z.string().nullable(),
    ssnLast4DueForVerification: z.boolean(),
    isSsnLast4Verified: z.boolean(),
  })
  .strict();

export const cmInfoRequestSchema = z
  .object({
    language: cmInfoLanguageRequestSchema,
    billingAddress: cmInfoBillingAddressRequestSchema,
    email: cmInfoEmailRequestSchema,
    p2D: cmInfoP2DRequestSchema,
    employmentIncome: cmInfoEmploymentIncomeRequestSchema,
    school: cmInfoSchoolRequestSchema,
    contactPreference: cmInfoContactPreferenceRequestSchema,
    security: cmInfoSecurityRequestSchema,
  })
  .strict();
